import React, { Suspense, useEffect, useState, lazy } from 'react'
import { NotFound, Redirect, Route, Switch, useHistory } from '@fs/zion-router'
import ErrorBoundary from '@fs/zion-error-boundary'
import { GroupsProvider, useGroupsContext } from '@fs/zion-tree-groups'
import TreeRoute from '@fs/zion-tree-route'
import { useUser } from '@fs/zion-user'
import getPermission from '@fs/zion-permissions'
import { NoticeLoading } from '@fs/zion-icon'
import { getActiveRole } from '@fs/zion-admin-mode'
import { useFeatureFlag } from '@fs/zion-flags'
import { AppLayout, colors } from '@fs/zion-ui'
import { SelectSiblingPedigreeButton, SelectSiblingPedigreeOverlay } from '@fs/zion-tree-pedigree'
import usePreloadPreferences from './services/usePreloadPreferences'
import TreeChangesPage from './pages/TreeChangesPage'
import GroupDetailsPage from './pages/GroupDetailsPage'
import ConnectCreatePage from './pages/ConnectCreatePage'
import FamilyGroupsListPage from './pages/FamilyGroupsListPage'
import UnavailableDecorator from './components/UnavailableDecorator'
import { SubnavWrapper } from './components/SubnavWrapper'
import DataAdminProvider from './providers/DataAdminProvider'
import AppLoading from './components/AppLoading'
import CenteredLoadingSpinner from './components/CenteredLoadingSpinner'
import TreeIdToGroupId from './pages/TreeIdToGroupId'
import ManageTreesPage from './pages/ManageTreesPage'
import UsabillaAction from './UsabillaAction'
import TreeSettingsPage, { SettingsPageSkeleton } from './pages/TreeSettingsPage'
import {
  CONNECT_CREATE_DONE_PATH,
  CONNECT_CREATE_PATH,
  FAMILY_GROUP_LIST_PATH,
  FAMILY_GROUP_PATH,
  OLD_RESEARCHER_PAGE_PATH,
  TREE_SETTINGS_PATH,
  SWITCH_TREE_PATH,
  TREE_CHANGES_PATH,
  TREES_PATH,
  RESEARCH_TREE_LIST_PATH,
  RESEARCH_TREE_PATH,
  TREE_SETTINGS_NO_TAB_PATH,
} from './routes'

// ---------------- Tree Import (migrated) lazy imports ----------------
// These were originally defined inside src/tree-import/App.js. We lazy load to keep initial bundle smaller.
const ComparePage = lazy(() => import('./tree-import/components/compare-page/ComparePage'))
const LandingPage = lazy(() => import('./tree-import/components/landing-page/LandingPage'))
const MatchesPage = lazy(() => import('./tree-import/components/matches-page/MatchesPage'))
const ChooseOldOrNewImportPage = lazy(() => import('./tree-import/components/import-page/ChooseOldOrNewImportPage'))
const RealTimeStats = lazy(() => import('./tree-import/components/admin/real-time-stats/RealTimeStats'))
const RecentTrees = lazy(() => import('./tree-import/components/admin/recent-trees/RecentTrees'))
const TreeStats = lazy(() => import('./tree-import/components/admin/tree-stats/TreeStats'))
const TreePersons = lazy(() => import('./tree-import/components/admin/tree-persons/TreePersons'))
const UserMatchesPage = React.lazy(() => import('./tree-import/components/matches-page/UserMatchesPage'))

const GroupsLoadingGate = ({ children, loader }) => {
  const { loading } = useGroupsContext()
  if (loading) {
    return <>{loader}</>
  }
  return children
}

// Wrapper component to allow the preference-fetching hook to run *before* the app loads, so that we catch everything on initial load, instead of piecemeal.
const TopLevelAppWrapper = ({ children }) => {
  // Pre-cache all preferences that are used in the app
  usePreloadPreferences()

  return (
    <GroupsProvider
      noLoadingGating
      LoadingComponent={AppLoading}
      SelectSiblingPedigreeButton={SelectSiblingPedigreeButton}
      SelectSiblingPedigreeOverlay={SelectSiblingPedigreeOverlay}
    >
      {children}
    </GroupsProvider>
  )
}

const DataAdminProviderWrapper = ({ children }) => {
  const [role, setRole] = useState()

  useEffect(() => {
    let isCurrent = true
    getActiveRole()
      .then((adminType) => isCurrent && setRole(adminType))
      .catch(console.error)
    return () => {
      isCurrent = false
    }
  }, [])

  return <DataAdminProvider dataAdminRole={role}>{children}</DataAdminProvider>
}

const ResearcherToResearchTreeRedirect = () => {
  // Instead of using <Redirect from="/researcher*" to="/research-tree*" /> that does not keep query params, we do this instead.
  const history = useHistory()

  useEffect(() => {
    history.replace(
      `${history.location.pathname.replace(OLD_RESEARCHER_PAGE_PATH, RESEARCH_TREE_LIST_PATH)}${history.location.search}`,
      history.location.state
    )
  }, [history])
  return null
}

// ---------------- Tree Import Routes (under /groups) ----------------
// These routes were migrated from the legacy tree import app. Previously they lived at /tree/import/*.
// Legacy server redirects now map /tree/import/* -> /groups/* (see server.js).
// Admin-only routes are guarded by permission checks below.

const AppRoutes = () => {
  const [isAdmin, setIsAdmin] = useState(false)
  const [permissionChecked, setPermissionChecked] = useState(false)

  useEffect(() => {
    let isCurrent = true
    getPermission('AdminPermission', 'SubmittedTrees')
      .then(({ authorized }) => {
        if (isCurrent) {
          setIsAdmin(authorized)
          setPermissionChecked(true)
        }
        return undefined
      })
      .catch((e) => {
        console.error(e)
        setPermissionChecked(true)
      })
    return () => {
      isCurrent = false
    }
  }, [])

  const { signedIn } = useUser()
  const manageTreesOn = useFeatureFlag('treeWeb_manageTrees').isOn
  const newSettingsPageOn = useFeatureFlag('treeWeb_groupManagement_newSettingsPage').isOn

  if (!permissionChecked) return <CenteredLoadingSpinner />

  return (
    <Switch>
      {/* Redirect to /trees when treeWeb_manageTrees is on */}
      {manageTreesOn && (
        <Route
          exact
          path={RESEARCH_TREE_LIST_PATH}
          render={({ location }) => <Redirect to={`${TREES_PATH}${location.search}`} />}
        />
      )}
      {manageTreesOn && <Route exact path="/files" render={() => <Redirect to={TREES_PATH} />} />}

      {/*
       * @deprecated The /files route below will be unreachable when treeWeb_manageTrees is persisted.
       * TODO: Remove this route and ChooseOldOrNewImportPage once flag is fully rolled out.
       */}
      <TreeRoute requirePersonalData exact path="/files" component={ChooseOldOrNewImportPage} />
      <TreeRoute requirePersonalData exact path="/compare/:treeId" component={ComparePage} />
      <TreeRoute requirePersonalData exact path="/matches/user" component={UserMatchesPage} />
      <TreeRoute requirePersonalData exact path="/matches/:treeId" component={MatchesPage} />

      {/* Import admin routes */}
      {isAdmin && <TreeRoute exact path="/admin" component={RealTimeStats} />}
      {isAdmin && <TreeRoute exact path="/admin/stats" component={RealTimeStats} />}
      {isAdmin && <TreeRoute exact path="/admin/stats/:treeId" component={TreeStats} />}
      {isAdmin && <TreeRoute exact path="/admin/recent/:page/:size" component={RecentTrees} />}
      {isAdmin && (
        <TreeRoute exact path="/admin/persons/:treeId/:personStatus/:matchLevel/:page/:size" component={TreePersons} />
      )}

      {/* Main Group Management routes */}
      {manageTreesOn && (
        <TreeRoute LoadingComponent={CenteredLoadingSpinner} requirePersonalData exact path={TREES_PATH}>
          <GroupsLoadingGate loader={<CenteredLoadingSpinner />}>
            <AppLayout backgroundColor={colors.gray03}>
              <ManageTreesPage />
            </AppLayout>
          </GroupsLoadingGate>
        </TreeRoute>
      )}
      {/*
       * @deprecated The /research-tree base route below will be unreachable when treeWeb_manageTrees is persisted.
       * Note: /research-tree/:groupId/* routes (group details, changes) will still work.
       * TODO: Remove this route once flag is fully rolled out.
       */}
      <TreeRoute LoadingComponent={CenteredLoadingSpinner} requirePersonalData exact path={RESEARCH_TREE_LIST_PATH}>
        <GroupsLoadingGate loader={<CenteredLoadingSpinner />}>
          <UnavailableDecorator isResearcherPage>
            <ChooseOldOrNewImportPage />
            <UsabillaAction type="cet" />
          </UnavailableDecorator>
        </GroupsLoadingGate>
      </TreeRoute>

      <TreeRoute LoadingComponent={CenteredLoadingSpinner} requirePersonalData exact path={FAMILY_GROUP_LIST_PATH}>
        <GroupsLoadingGate loader={<CenteredLoadingSpinner />}>
          <UnavailableDecorator>
            <FamilyGroupsListPage />
            <UsabillaAction />
          </UnavailableDecorator>
        </GroupsLoadingGate>
      </TreeRoute>

      <TreeRoute LoadingComponent={CenteredLoadingSpinner} requirePersonalData exact path={FAMILY_GROUP_PATH}>
        <GroupsLoadingGate loader={<CenteredLoadingSpinner />}>
          <UnavailableDecorator>
            <GroupDetailsPage />
            <UsabillaAction />
          </UnavailableDecorator>
        </GroupsLoadingGate>
      </TreeRoute>

      {newSettingsPageOn && <Redirect exact from={TREE_SETTINGS_NO_TAB_PATH} to="/:subPage/:groupId/details" />}

      {newSettingsPageOn ? (
        <TreeRoute LoadingComponent={CenteredLoadingSpinner} requirePersonalData exact path={TREE_SETTINGS_PATH}>
          <UnavailableDecorator isResearcherPage loader={<SettingsPageSkeleton />}>
            <TreeSettingsPage />
            <UsabillaAction type="cet" />
          </UnavailableDecorator>
        </TreeRoute>
      ) : (
        <TreeRoute LoadingComponent={CenteredLoadingSpinner} requirePersonalData exact path={RESEARCH_TREE_PATH}>
          <GroupsLoadingGate loader={<CenteredLoadingSpinner />}>
            <UnavailableDecorator isResearcherPage>
              <GroupDetailsPage />
              <UsabillaAction type="cet" />
            </UnavailableDecorator>
          </GroupsLoadingGate>
        </TreeRoute>
      )}

      <TreeRoute LoadingComponent={CenteredLoadingSpinner} path={OLD_RESEARCHER_PAGE_PATH}>
        <GroupsLoadingGate loader={<CenteredLoadingSpinner />}>
          <ResearcherToResearchTreeRedirect />
        </GroupsLoadingGate>
      </TreeRoute>

      <TreeRoute LoadingComponent={CenteredLoadingSpinner} requirePersonalData exact path={TREE_CHANGES_PATH}>
        <GroupsLoadingGate loader={<CenteredLoadingSpinner />}>
          <UnavailableDecorator isResearcherPage>
            <TreeChangesPage />
            <UsabillaAction type="cet" />
          </UnavailableDecorator>
        </GroupsLoadingGate>
      </TreeRoute>
      <TreeRoute LoadingComponent={CenteredLoadingSpinner} requirePersonalData exact path={SWITCH_TREE_PATH}>
        <GroupsLoadingGate loader={<CenteredLoadingSpinner />}>
          <TreeIdToGroupId />
        </GroupsLoadingGate>
      </TreeRoute>
      <TreeRoute LoadingComponent={CenteredLoadingSpinner} requirePersonalData exact path={CONNECT_CREATE_PATH}>
        <GroupsLoadingGate loader={<CenteredLoadingSpinner />}>
          <UnavailableDecorator>
            <ConnectCreatePage />
          </UnavailableDecorator>
        </GroupsLoadingGate>
      </TreeRoute>

      <Route exact path={CONNECT_CREATE_DONE_PATH} />

      {/* Default import landing (signed-in users go to /family, others see landing page) */}
      <Route exact path="/" requirePersonalData>
        {signedIn ? (
          <Redirect to={FAMILY_GROUP_LIST_PATH} />
        ) : (
          <Suspense
            fallback={
              <div style={{ margin: '16px auto', display: 'flex', justifyContent: 'center' }}>
                <NoticeLoading size="lg" />
              </div>
            }
          >
            <LandingPage />
          </Suspense>
        )}
      </Route>
      {/* Group Management 404 inside /groups namespace */}
      <Route>
        <NotFound />
      </Route>
    </Switch>
  )
}

export default function App() {
  return (
    <TopLevelAppWrapper>
      <SubnavWrapper />
      <main id="skipToMainContent" style={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <DataAdminProviderWrapper>
          <ErrorBoundary>
            <Suspense fallback={<CenteredLoadingSpinner />}>
              <AppRoutes />
            </Suspense>
          </ErrorBoundary>
        </DataAdminProviderWrapper>
      </main>
    </TopLevelAppWrapper>
  )
}
